import type {CSSProperties} from 'react';
import {Toaster as Sonner, toast as sonnerToast, type ToastClassnames, type ToasterProps} from 'sonner';
import {FailIcon, InfoIcon, SpinnerIcon, Success2Icon, WarningIcon} from '../icons';

export const toast: typeof sonnerToast = Object.assign((...args: Parameters<typeof sonnerToast>) => sonnerToast(...args), {
	...sonnerToast,
	error: (message: Parameters<typeof sonnerToast.error>[0], options?: Parameters<typeof sonnerToast.error>[1]) =>
		sonnerToast.error(message, {duration: Infinity, closeButton: true, ...options}),
	loading: (message: Parameters<typeof sonnerToast.loading>[0], options?: Parameters<typeof sonnerToast.loading>[1]) => {
		const id = sonnerToast.loading(message, options);
		if (options?.duration && options.duration !== Infinity)
			setTimeout(() => {
				if (isStillLoading(id)) sonnerToast.dismiss(id);
			}, options.duration);

		return id;
	}
});

const isStillLoading = (id: string | number) =>
	sonnerToast.getToasts().some(entry => entry.id === id && 'type' in entry && entry.type === 'loading');

export const Toaster = (props: ToasterProps) => (
	<Sonner closeButton icons={icons} style={sonnerVars} toastOptions={{classNames: typeStyles}} {...props}/>
);

Toaster.displayName = 'Toaster';

const icons = {
	success: <Success2Icon color='success'/>,
	info: <InfoIcon color='info'/>,
	warning: <WarningIcon color='medium'/>,
	error: <FailIcon color='critical'/>,
	loading: <SpinnerIcon className='animate-spin'/>
};

const sonnerVars = {
	'--normal-bg': 'var(--xx-background-800)',
	'--normal-text': 'var(--xx-foreground-contrast)',
	'--normal-border': 'var(--xx-foreground-400)',
	'--border-radius': '0.375rem',
	'--toast-close-button-start': 'unset',
	'--toast-close-button-end': '0',
	'--toast-close-button-transform': 'translate(35%, -35%)',
	'--gray2': 'var(--xx-background-700)',
	'--gray4': 'var(--xx-foreground-400)',
	'--gray5': 'var(--xx-foreground-300)',
	'--gray12': 'var(--xx-foreground-contrast)'
} as CSSProperties;

const typeStyles: ToastClassnames = {
	toast: 'v3 !border-l-4 [&_svg]:size-5',
	icon: '!size-5',
	default: '!border-l-foreground-300',
	success: '!border-l-tone-green',
	error: '!border-l-tone-red',
	warning: '!border-l-tone-orange',
	info: '!border-l-tone-teal',
	loading: '!border-l-brand-main'
};

export type {ToasterProps};
